import React from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { alpha, useTheme } from "@mui/material/styles";
import { useDesignTokens } from "../../../theme/useDesignTokens";
import useClearAlertsOnChange from "../../../hooks/useClearAlertsOnChange";

const getAppearanceStyles = (appearance, theme, colors) => {
  const primaryColor = colors?.primary?.main || theme.palette.primary.main;
  const primaryText = colors?.primary?.text || theme.palette.primary.contrastText || "#FFFFFF";
  const primaryBackground = colors?.primary?.bg || alpha(primaryColor, 0.08);

  if (appearance === "accent") {
    return {
      defaultColor: "#333333",
      selectedColor: primaryColor,
      selectedBackground: primaryBackground,
      selectedBorder: `1px solid ${alpha(primaryColor, 0.28)}`,
      indicatorColor: primaryColor,
    };
  }

  return {
    defaultColor: "#333333",
    selectedColor: primaryText,
    selectedBackground: primaryColor,
    selectedBorder: `1px solid ${primaryColor}`,
    indicatorColor: primaryColor,
  };
};

const AppTabs = ({
  tabs = [],
  value = 0,
  onChange,
  appearance = "neutral",
  disableRipple = true,
  tabMinWidth = "auto",
  textTransform = "capitalize",
  alignLeft = false,
  firstTabFlush = false,
  showScrollButtons = false,
  scrollButtons = "auto",
  allowScrollButtonsMobile = false,
  tabsSx = {},
  tabSx = {},
  selectedTabSx = {},
  TabIndicatorProps,
  ...rest
}) => {
  const theme = useTheme();
  const { colors } = useDesignTokens();
  const appearanceStyles = getAppearanceStyles(appearance, theme, colors);
  const selectedStateSx = {
    color: `${appearanceStyles.selectedColor} !important`,
    backgroundColor: `${appearanceStyles.selectedBackground} !important`,
    border: `${appearanceStyles.selectedBorder} !important`,
  };

  useClearAlertsOnChange(value);

  return (
    <Tabs
      value={value}
      onChange={onChange}
      scrollButtons={showScrollButtons ? scrollButtons : false}
      allowScrollButtonsMobile={
        showScrollButtons ? allowScrollButtonsMobile : false
      }
      TabIndicatorProps={{
        ...TabIndicatorProps,
        sx: {
          backgroundColor: appearanceStyles.indicatorColor,
          ...(TabIndicatorProps?.sx || {}),
        },
      }}
      sx={{
        "& .MuiTab-root": {
          color: `${appearanceStyles.defaultColor} !important`,
          WebkitTapHighlightColor: "transparent",
        },
        "& .MuiTab-root.Mui-selected": {
          ...selectedStateSx,
        },
        "& .MuiTab-root:hover, & .MuiTab-root:active, & .MuiTab-root:focus, & .MuiTab-root.Mui-focusVisible": {
          color: `${appearanceStyles.defaultColor} !important`,
          backgroundColor: "transparent !important",
          border: "1px solid transparent !important",
          outline: "none !important",
          boxShadow: "none !important",
        },
        "& .MuiTab-root.Mui-selected:hover, & .MuiTab-root.Mui-selected:active, & .MuiTab-root.Mui-selected:focus, & .MuiTab-root.Mui-selected.Mui-focusVisible": {
          ...selectedStateSx,
          outline: "none !important",
          boxShadow: "none !important",
        },
        "& .MuiTabs-indicator": {
          backgroundColor: `${appearanceStyles.indicatorColor} !important`,
        },
        "& .MuiTab-root .MuiTouchRipple-root .MuiTouchRipple-child": {
          backgroundColor: `${alpha(appearanceStyles.indicatorColor, 0.22)} !important`,
        },
        ...tabsSx,
      }}
      {...rest}
    >
      {tabs.map((tab, index) => {
        const tabValue = tab.value ?? index;
        const isSelected = value === tabValue;

        return (
          <Tab
            key={tab.key || `${tab.label}-${tabValue}`}
            value={tabValue}
            label={tab.label}
            disabled={tab.disabled}
            wrapped={tab.wrapped}
            icon={tab.icon}
            iconPosition={tab.iconPosition}
            disableRipple={disableRipple}
            disableFocusRipple={disableRipple}
            sx={{
              minWidth: tabMinWidth,
              px: 2,
              color: `${appearanceStyles.defaultColor} !important`,
              backgroundColor: "transparent",
              border: "1px solid transparent",
              outline: "none",
              boxShadow: "none",
              textTransform,
              justifyContent: alignLeft ? "flex-start" : "center",
              alignItems: alignLeft ? "flex-start" : "center",
              textAlign: alignLeft ? "left" : "center",
              WebkitTapHighlightColor: "transparent",
              "&:hover, &:active, &:focus, &.Mui-focusVisible": {
                color: `${appearanceStyles.defaultColor} !important`,
                backgroundColor: "transparent !important",
                border: "1px solid transparent !important",
                outline: "none !important",
                boxShadow: "none !important",
              },
              ...(firstTabFlush && index === 0 ? { pl: 0 } : {}),
              ...tabSx,
              ...(tab.sx || {}),
              ...(isSelected
                ? {
                    ...selectedStateSx,
                    "&:hover, &:active, &:focus, &.Mui-focusVisible": {
                      ...selectedStateSx,
                      outline: "none !important",
                      boxShadow: "none !important",
                    },
                    ...selectedTabSx,
                  }
                : null),
            }}
            {...(tab.tabProps || {})}
          />
        );
      })}
    </Tabs>
  );
};

export default AppTabs;
